import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createContainer } from 'react-tracked';
import { resources } from '../constants';
import { useGlobalState } from '../state/GlobalStateProvider';
import { filterObject, formatDecimals, trackClairtyEvent, truncateEthereumAddress } from '../utils';
import { useBackendApi } from './backendHelper';
import { getENSData } from './ensHelper';

const useMrepHelper = () => {
  const backendHelper = useBackendApi();
  const { userAddress } = useParams();
  const [{ user }] = useGlobalState();
  const [state, setState] = useState();

  const setValue = (key, value) => setState(state => ({ ...state, [key]: value }));

  async function getIndividualProtocol(protocol, userAddress, filter) {
    try {
      const body = {
        address: userAddress,
        filter: {
          protocol,
          ...filterObject(filter || {}),
        },
      };
      const data = await backendHelper.mrep.getProtocolWiseMrepData(body);
      return data?.data;
    } catch (error) {
      return null;
    }
  }

  async function getProtocolData(userAddress) {
    try {
      setValue('protocolRankingLoading', true);
      const body = {
        address: userAddress,
        filter: {
          ...filterObject({
            chainId: state?.protocolFilter?.chainId,
            actionType: state?.protocolFilter?.actionType,
            timeDeltas: ['7d', '30d'],
          }),
        },
      };
      const data = await backendHelper.mrep.getProtocolWiseMrepData(body);
      setValue('protocolRanking', data?.data?.rankings || []);
      setValue('protocolRankingLoading', false);
    } catch (error) {
      setValue('protocolRanking', null);
      setValue('protocolRankingLoading', false);
    }
  }

  async function getChainWiseData(userAddress) {
    try {
      setValue('chainRankingLoading', true);
      const body = {
        address: userAddress,
        filter: {
          ...filterObject({
            chainId: state?.chainFilter?.chainId,
            actionType: state?.chainFilter?.action,
            timeDeltas: ['7d', '30d'],
          }),
        },
      };
      const data = await backendHelper.mrep.getChainWiseMrepData(body);
      setValue('chainRanking', data?.data?.rankings || []);
      setValue('chainRankingLoading', false);
    } catch (error) {
      setValue('chainData', null);
      setValue('chainRankingLoading', false);
    }
  }

  async function getActionStats(data, userAddress) {
    try {
      let filter = {
        address: userAddress,
      };
      if (data) {
        filter = {
          ...filter,
          filter: {
            ...filterObject(data || {}),
          },
        };
      }
      const userData = await backendHelper.mrep.getActionStats(filter);

      return userData.data;
    } catch (error) {
      console.log(error);
      return {};
    }
  }

  async function spectateUser(address) {
    try {
      setValue('userData', null);
      const userData = await backendHelper.user.spectateUser(address);
      if (userData?.data?.user) {
        return setValue('userData', userData?.data?.user);
      }
      const ensData = await getENSData(userAddress);
      return setValue('userData', {
        address: String(userAddress).toLowerCase(),
        name: ensData?.ensName || truncateEthereumAddress(userAddress),
        avatarUrl: ensData.ensAvatar || resources.img.getAvatar(userAddress),
      });
    } catch (error) {}
  }

  useEffect(() => {
    getProtocolData(userAddress);
    setValue('protocolActionStats', null);
    getActionStats(state?.protocolFilter, userAddress).then(data => {
      setValue('protocolActionStats', data);

      if (user?.isFirstLogin) {
        trackClairtyEvent('account_created', {
          totalVolumeUSD: formatDecimals(+data?.swap?.volume + +data?.bridge?.volume),
        });
      }
      
    });
  }, [state?.protocolFilter?.chainId, userAddress]);

  useEffect(() => {
    if (state?.protocolFilter?.actionType !== undefined) {
      getProtocolData(userAddress);
    }
  }, [state?.protocolFilter?.actionType, userAddress]);

  useEffect(() => {
    getChainWiseData(userAddress);
  }, [state?.chainFilter, userAddress]);

  useEffect(() => {
    if (userAddress) {
      getActionStats(state?.chainFilter, userAddress).then(res => {
        setValue('chainActionStats', res);
      });
    }
  }, [userAddress]);

  useEffect(() => {
    if (userAddress && user) {
      if (userAddress === user?.address) {
        setValue('userData', user);
      } else {
        spectateUser(userAddress);
      }
    }
  }, [userAddress, user]);

  return [{ ...state, getActionStats, setValue, getIndividualProtocol }, setState];
};

const { Provider, useTracked } = createContainer(() => useMrepHelper());

export const useMrepState = useTracked;
export const MrepStateProvider = ({ children }) => <Provider>{children}</Provider>;
export default MrepStateProvider;
